import React, { useEffect, useState, useRef } from 'react'; // Import useRef
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, Card, CardContent, Divider } from '@mui/material';
import { toast } from 'react-toastify';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import COLORS from '../../constants/colors';
import baseURL from '../pages/user/config';
import Footer from '../pages/user/Reusbale/Footer';

const PaymentSuccess = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // 'success' | 'failed'
    const [data, setData] = useState(null);
    const searchParams = new URLSearchParams(location.search);
    const reference = searchParams.get('reference');
    const toastShown = useRef(false); // Use a ref to track toast state


    useEffect(() => {
        if (!reference) {
            if (!toast.isActive("no-ref")) { // Check if a toast with this ID is active
                toast.error('No transaction reference found.', { toastId: "no-ref" });
            }
            setStatus('failed');
            setLoading(false);
            return;
        }

        const verifyTransaction = async () => {
            try {
                const response = await fetch(`${baseURL}/data/backend/paystack/verify/${reference}`);
                const result = await response.json();

                setStatus(result.status);
                if (result.status === 'success') {
                    setData(result.data);
                    if (!toastShown.current) {
                        toast.success(result.message);
                        toastShown.current = true;
                    }
                } else {
                    if (!toastShown.current) {
                        toast.error(result.message);
                        toastShown.current = true;
                    }
                }
            } catch (error) {
                console.error('Verification error:', error);
                setStatus('failed');
                if (!toastShown.current) {
                    toast.error('An error occurred while verifying payment.');
                    toastShown.current = true;
                }
            } finally {
                setLoading(false);
            }
        };

        verifyTransaction();
    }, [reference]);


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column">
                <CircularProgress color="primary" size={18} />
                <Typography variant="h6" sx={{ mt: 2, color: COLORS.textSecondary }}>
                    Verifying your payment...
                </Typography>
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2} flexDirection="column">
            <Card sx={{ p: 4, borderRadius: '20px', maxWidth: 500, width: '100%', boxShadow: 0 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                    {status === 'success' ? (
                        <>
                            <CheckCircleOutline sx={{ fontSize: 64, color: 'green', mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Payment Successful!
                            </Typography>
                            {data.metadata?.name && (
                                <Typography variant="subtitle1" sx={{ mb: 1, color: COLORS.textSecondary }}>
                                    Thank you, <strong>{data.metadata.name}</strong>! for your generous Donation
                                </Typography>
                            )}
                            {data.metadata?.campaignName && (
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Donation for: <strong>{data.metadata.campaignName}</strong>
                                </Typography>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body1" sx={{ color: COLORS.textSecondary }}>
                                Reference: <strong>{data.reference}</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, color: COLORS.textSecondary }}>
                                Amount: <strong>${(data.amount / 100).toFixed(2)}</strong>
                            </Typography>
                        </>
                    ) : (
                        <>
                            <ErrorOutline sx={{ fontSize: 64, color: 'red', mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'red' }}>
                                Payment Failed
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, color: COLORS.textSecondary }}>
                                Something went wrong. Please try again or contact support.
                            </Typography>
                        </>
                    )}
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: COLORS.accent,
                            color: COLORS.white,
                            borderRadius: '25px',
                            px: 3,
                            py: 1,
                            boxShadow: 0,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            '&:hover': {
                                boxShadow: 0,
                                backgroundColor: COLORS.accentDark,
                            },
                            mt: 1,
                            flexShrink: 0,
                        }}
                        onClick={() => navigate('/')}
                    >
                        Go to Home
                    </Button>
                </CardContent>
            </Card>
            <Footer />
        </Box>
    );
};

export default PaymentSuccess;